import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@ui-nuvertos/shared';

export interface Center {
  id: number;
  code: string;
  name: string;
  location?: string;
  description?: string;
  is_active: boolean;
  is_deleted: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface CentersResponse {
  data: {
    centers: Center[];
    total: number;
    limit: number;
    offset: number;
  };
  message: string;
  status: string;
}

export interface SwitchCenterRequest {
  center_id: number;
}

export interface SwitchCenterResponse {
  message: string;
  access_token: string;
  center_id: number;
  role?: string;
}

@Injectable({
  providedIn: 'root'
})
export class CenterService {
  private readonly API_BASE_URL = environment.apiUrl;
  private http = inject(HttpClient);

  /**
   * Get all centers accessible to the current user
   * @param limit - Number of centers to return (default: 10)
   * @param offset - Number of centers to skip (default: 0)
   * @param isDeleted - Filter by deleted status (default: false)
   */
  getCenters(limit: number = 10, offset: number = 0, isDeleted: boolean = false): Observable<CentersResponse> {
    const params = new HttpParams()
      .set('limit', limit.toString())
      .set('offset', offset.toString())
      .set('is_deleted', isDeleted.toString());

    return this.http.get<CentersResponse>(`${this.API_BASE_URL}/centers/`, {
      params,
      withCredentials: true
    });
  }

  /**
   * Get a specific center by ID
   * @param centerId - The center ID to retrieve
   */
  getCenterById(centerId: number): Observable<{ data: Center }> {
    return this.http.get<{ data: Center }>(`${this.API_BASE_URL}/centers/${centerId}`, {
      withCredentials: true
    });
  }

  /**
   * Switch to a different center
   * @param centerId - The center ID to switch to
   */
  switchCenter(centerId: number): Observable<SwitchCenterResponse> {
    const body: SwitchCenterRequest = { center_id: centerId };
    return this.http.post<SwitchCenterResponse>(`${this.API_BASE_URL}/auth/switch-center`, body, {
      withCredentials: true
    });
  }
}

